import { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Button } from './ui/button';
import { Slider } from './ui/slider';
import { Badge } from './ui/badge';
import { toast } from 'sonner';
import axios from 'axios';
import { 
    Moon, Droplets, Dumbbell, Utensils, Sparkles, 
    ChevronRight, ChevronLeft, Loader2, CheckCircle2,
    Coffee, Wine, GlassWater
} from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL;

// Nanouk images
const NANOUK = {
    curious: 'https://images.unsplash.com/photo-1767381604151-bae00f2fb337?w=200&h=200&fit=crop',
    happy: 'https://images.unsplash.com/photo-1767381604151-bae00f2fb337?w=200&h=200&fit=crop',
};

export default function OnboardingBackfill({ onComplete, language = 'de' }) {
    const [step, setStep] = useState(0);
    const [loading, setLoading] = useState(false);
    const [complete, setComplete] = useState(false);
    
    const [habits, setHabits] = useState({
        // Sleep
        typical_sleep_time: '23:00',
        typical_wake_time: '07:00',
        sleep_quality_avg: 7,
        // Water
        daily_water_ml: 2000,
        // Coffee & Alcohol & Lemonade
        coffee_cups_per_day: 2,
        wine_glasses_per_week: 3,
        lemonade_glasses_per_day: 0,
        lemonade_sweetened: true,
        // Workout
        workouts_per_week: 3,
        typical_workout_type: 'cardio',
        typical_workout_duration: 45,
        // Nutrition
        meals_per_day: 3,
        daily_calories_avg: 2000,
        // Days
        days_to_fill: 30
    });

    const t = {
        de: {
            title: 'Erzähl mir von dir!',
            subtitle: 'Damit ich dir sofort helfen kann, fülle ich deine Historie mit typischen Werten.',
            
            // Step 1 - Sleep
            sleepTitle: 'Wie schläfst du normalerweise?',
            sleepTime: 'Schlafenszeit',
            wakeTime: 'Aufwachzeit',
            sleepQuality: 'Schlafqualität (1-10)',
            
            // Step 2 - Water
            waterTitle: 'Wie viel trinkst du täglich?',
            waterAmount: 'Wassermenge pro Tag',
            waterUnit: 'Liter',
            
            // Step 3 - Coffee & Alcohol & Lemonade
            lifestyleTitle: 'Getränke & Genuss',
            coffeeCups: 'Tassen Kaffee pro Tag',
            wineGlasses: 'Gläser Wein pro Woche',
            lemonadeGlasses: 'Gläser Limonade pro Tag',
            lemonadeSweetened: 'Limonade gesüsst',
            lemonadeUnsweetened: 'Limonade ungesüsst',
            coffeeNote: 'Koffein beeinflusst deinen Schlaf',
            alcoholNote: 'Alkohol beeinflusst deine Erholung',
            lemonadeNote: 'Zucker beeinflusst deinen Energielevel',
            
            // Step 4 - Workout
            workoutTitle: 'Wie oft trainierst du?',
            workoutsPerWeek: 'Trainings pro Woche',
            workoutType: 'Bevorzugte Trainingsart',
            workoutDuration: 'Durchschnittliche Dauer',
            workoutTypes: {
                strength: 'Krafttraining',
                cardio: 'Cardio/Laufen',
                hiit: 'HIIT',
                yoga: 'Yoga',
                swimming: 'Schwimmen',
                cycling: 'Radfahren',
                walking: 'Spazieren'
            },
            
            // Step 4 - Nutrition
            nutritionTitle: 'Wie ernährst du dich?',
            mealsPerDay: 'Mahlzeiten pro Tag',
            caloriesPerDay: 'Kalorien pro Tag (ungefähr)',
            
            // Navigation
            next: 'Weiter',
            back: 'Zurück',
            generate: 'Daten generieren',
            skip: 'Überspringen',
            
            // Completion
            generating: 'Nanouk erstellt deine Historie...',
            completeTitle: 'Perfekt! 🎉',
            completeSubtitle: 'Deine letzten 30 Tage sind jetzt gefüllt!',
            completeButton: 'Los geht\'s!',
            
            // Stats
            daysGenerated: 'Tage generiert',
            entries: 'Einträge erstellt'
        },
        en: {
            title: 'Tell me about yourself!',
            subtitle: 'So I can help you right away, I\'ll fill your history with typical values.',
            
            sleepTitle: 'How do you usually sleep?',
            sleepTime: 'Bedtime',
            wakeTime: 'Wake time',
            sleepQuality: 'Sleep quality (1-10)',
            
            waterTitle: 'How much do you drink daily?',
            waterAmount: 'Water per day',
            waterUnit: 'Liters',
            
            // Step 3 - Coffee, Alcohol & Lemonade (English)
            lifestyleTitle: 'Drinks & Lifestyle',
            coffeeCups: 'Cups of coffee per day',
            wineGlasses: 'Glasses of wine per week',
            lemonadeGlasses: 'Glasses of lemonade per day',
            lemonadeSweetened: 'Sweetened lemonade',
            lemonadeUnsweetened: 'Unsweetened lemonade',
            coffeeNote: 'Caffeine affects your sleep',
            alcoholNote: 'Alcohol affects your recovery',
            lemonadeNote: 'Sugar affects your energy levels',
            
            workoutTitle: 'How often do you work out?',
            workoutsPerWeek: 'Workouts per week',
            workoutType: 'Preferred workout type',
            workoutDuration: 'Average duration',
            workoutTypes: {
                strength: 'Strength Training',
                cardio: 'Cardio/Running',
                hiit: 'HIIT',
                yoga: 'Yoga',
                swimming: 'Swimming',
                cycling: 'Cycling',
                walking: 'Walking'
            },
            
            nutritionTitle: 'How do you eat?',
            mealsPerDay: 'Meals per day',
            caloriesPerDay: 'Calories per day (approx)',
            
            next: 'Next',
            back: 'Back',
            generate: 'Generate Data',
            skip: 'Skip',
            
            generating: 'Nanouk is creating your history...',
            completeTitle: 'Perfect! 🎉',
            completeSubtitle: 'Your last 30 days are now filled!',
            completeButton: 'Let\'s go!',
            
            daysGenerated: 'Days generated',
            entries: 'Entries created'
        }
    }[language] || {};

    const [result, setResult] = useState(null);

    const generateData = async () => {
        setLoading(true);
        try {
            const response = await axios.post(`${API}/api/backfill/generate`, habits, {
                timeout: 60000, // 60s — generates ~300 entries server-side
            });
            setResult(response.data);
            setComplete(true);
            toast.success('Historische Daten erfolgreich erstellt!');
        } catch (error) {
            console.error('Backfill error:', error);
            const detail =
                error.response?.data?.detail
                || error.message
                || 'Unbekannter Fehler';
            const msg = error.code === 'ECONNABORTED'
                ? 'Server antwortet nicht — bitte nochmal versuchen.'
                : `Fehler beim Generieren: ${detail}`;
            toast.error(msg, { duration: 6000 });
        } finally {
            setLoading(false);
        }
    };

    const steps = [
        // Step 1: Sleep
        {
            icon: Moon,
            color: 'from-indigo-500 to-violet-500',
            title: t.sleepTitle,
            content: (
                <div className="space-y-6">
                    <div className="grid grid-cols-2 gap-4">
                        <div>
                            <label className="text-sm font-medium mb-2 block">{t.sleepTime}</label>
                            <input
                                type="time"
                                value={habits.typical_sleep_time}
                                onChange={(e) => setHabits({...habits, typical_sleep_time: e.target.value})}
                                className="w-full p-3 rounded-xl border bg-background"
                            />
                        </div>
                        <div>
                            <label className="text-sm font-medium mb-2 block">{t.wakeTime}</label>
                            <input
                                type="time"
                                value={habits.typical_wake_time}
                                onChange={(e) => setHabits({...habits, typical_wake_time: e.target.value})}
                                className="w-full p-3 rounded-xl border bg-background"
                            />
                        </div>
                    </div>
                    <div>
                        <label className="text-sm font-medium mb-2 block">
                            {t.sleepQuality}: {habits.sleep_quality_avg}
                        </label>
                        <Slider
                            value={[habits.sleep_quality_avg]}
                            onValueChange={([v]) => setHabits({...habits, sleep_quality_avg: v})}
                            min={1}
                            max={10}
                            step={1}
                            className="py-4"
                        />
                    </div>
                </div>
            )
        },
        // Step 2: Water
        {
            icon: Droplets,
            color: 'from-cyan-500 to-blue-500',
            title: t.waterTitle,
            content: (
                <div className="space-y-6">
                    <div className="text-center">
                        <span className="text-5xl font-bold text-cyan-500">
                            {(habits.daily_water_ml / 1000).toFixed(1)}
                        </span>
                        <span className="text-2xl text-white/60 ml-2">{t.waterUnit}</span>
                    </div>
                    <Slider
                        value={[habits.daily_water_ml]}
                        onValueChange={([v]) => setHabits({...habits, daily_water_ml: v})}
                        min={500}
                        max={4000}
                        step={250}
                        className="py-4"
                    />
                    <div className="flex justify-between text-xs text-white/60">
                        <span>0.5L</span>
                        <span>2L</span>
                        <span>4L</span>
                    </div>
                </div>
            )
        },
        // Step 3: Coffee, Alcohol & Lemonade
        {
            icon: Coffee,
            color: 'from-amber-500 to-orange-500',
            title: t.lifestyleTitle,
            content: (
                <div className="space-y-5">
                    {/* Coffee */}
                    <div>
                        <label className="text-sm font-medium mb-2 block flex items-center gap-2">
                            <Coffee className="w-4 h-4 text-amber-600" />
                            {t.coffeeCups}: {habits.coffee_cups_per_day}
                        </label>
                        <Slider
                            value={[habits.coffee_cups_per_day]}
                            onValueChange={([v]) => setHabits({...habits, coffee_cups_per_day: v})}
                            min={0}
                            max={8}
                            step={1}
                            className="py-3"
                        />
                        <p className="text-xs text-white/60 mt-1">☕ {t.coffeeNote}</p>
                    </div>
                    
                    {/* Wine/Alcohol */}
                    <div>
                        <label className="text-sm font-medium mb-2 block flex items-center gap-2">
                            <Wine className="w-4 h-4 text-red-500" />
                            {t.wineGlasses}: {habits.wine_glasses_per_week}
                        </label>
                        <Slider
                            value={[habits.wine_glasses_per_week]}
                            onValueChange={([v]) => setHabits({...habits, wine_glasses_per_week: v})}
                            min={0}
                            max={14}
                            step={1}
                            className="py-3"
                        />
                        <p className="text-xs text-white/60 mt-1">🍷 {t.alcoholNote}</p>
                    </div>
                    
                    {/* Lemonade */}
                    <div>
                        <label className="text-sm font-medium mb-2 block flex items-center gap-2">
                            <GlassWater className="w-4 h-4 text-yellow-500" />
                            {t.lemonadeGlasses}: {habits.lemonade_glasses_per_day}
                        </label>
                        <Slider
                            value={[habits.lemonade_glasses_per_day]}
                            onValueChange={([v]) => setHabits({...habits, lemonade_glasses_per_day: v})}
                            min={0}
                            max={6}
                            step={1}
                            className="py-3"
                        />
                        {habits.lemonade_glasses_per_day > 0 && (
                            <div className="flex gap-2 mt-2">
                                <button
                                    onClick={() => setHabits({...habits, lemonade_sweetened: true})}
                                    className={`flex-1 p-2 rounded-lg text-sm font-medium transition-all ${
                                        habits.lemonade_sweetened
                                            ? 'bg-gradient-to-r from-yellow-400 to-orange-400 text-white'
                                            : 'bg-secondary hover:bg-secondary/80'
                                    }`}
                                >
                                    🍬 {t.lemonadeSweetened}
                                </button>
                                <button
                                    onClick={() => setHabits({...habits, lemonade_sweetened: false})}
                                    className={`flex-1 p-2 rounded-lg text-sm font-medium transition-all ${
                                        !habits.lemonade_sweetened
                                            ? 'bg-gradient-to-r from-green-400 to-emerald-400 text-white'
                                            : 'bg-secondary hover:bg-secondary/80'
                                    }`}
                                >
                                    🥒 {t.lemonadeUnsweetened}
                                </button>
                            </div>
                        )}
                        <p className="text-xs text-white/60 mt-1">🍋 {t.lemonadeNote}</p>
                    </div>
                </div>
            )
        },
        // Step 4: Workout
        {
            icon: Dumbbell,
            color: 'from-orange-500 to-red-500',
            title: t.workoutTitle,
            content: (
                <div className="space-y-6">
                    <div>
                        <label className="text-sm font-medium mb-2 block">
                            {t.workoutsPerWeek}: {habits.workouts_per_week}x
                        </label>
                        <Slider
                            value={[habits.workouts_per_week]}
                            onValueChange={([v]) => setHabits({...habits, workouts_per_week: v})}
                            min={0}
                            max={7}
                            step={1}
                            className="py-4"
                        />
                    </div>
                    <div>
                        <label className="text-sm font-medium mb-2 block">{t.workoutType}</label>
                        <div className="grid grid-cols-2 gap-2">
                            {Object.entries(t.workoutTypes || {}).map(([key, label]) => (
                                <button
                                    key={key}
                                    onClick={() => setHabits({...habits, typical_workout_type: key})}
                                    className={`p-3 rounded-xl text-sm font-medium transition-all ${
                                        habits.typical_workout_type === key
                                            ? 'bg-gradient-to-r from-orange-500 to-red-500 text-white'
                                            : 'bg-secondary hover:bg-secondary/80'
                                    }`}
                                >
                                    {label}
                                </button>
                            ))}
                        </div>
                    </div>
                    <div>
                        <label className="text-sm font-medium mb-2 block">
                            {t.workoutDuration}: {habits.typical_workout_duration} min
                        </label>
                        <Slider
                            value={[habits.typical_workout_duration]}
                            onValueChange={([v]) => setHabits({...habits, typical_workout_duration: v})}
                            min={15}
                            max={120}
                            step={5}
                            className="py-4"
                        />
                    </div>
                </div>
            )
        },
        // Step 4: Nutrition
        {
            icon: Utensils,
            color: 'from-green-500 to-emerald-500',
            title: t.nutritionTitle,
            content: (
                <div className="space-y-6">
                    <div>
                        <label className="text-sm font-medium mb-2 block">
                            {t.mealsPerDay}: {habits.meals_per_day}
                        </label>
                        <Slider
                            value={[habits.meals_per_day]}
                            onValueChange={([v]) => setHabits({...habits, meals_per_day: v})}
                            min={1}
                            max={6}
                            step={1}
                            className="py-4"
                        />
                    </div>
                    <div>
                        <label className="text-sm font-medium mb-2 block">
                            {t.caloriesPerDay}: {habits.daily_calories_avg} kcal
                        </label>
                        <Slider
                            value={[habits.daily_calories_avg]}
                            onValueChange={([v]) => setHabits({...habits, daily_calories_avg: v})}
                            min={1200}
                            max={4000}
                            step={100}
                            className="py-4"
                        />
                    </div>
                </div>
            )
        }
    ];

    const currentStep = steps[step];

    if (complete) {
        return (
            <motion.div
                initial={{ opacity: 0, scale: 0.9 }}
                animate={{ opacity: 1, scale: 1 }}
                className="min-h-screen bg-gradient-to-b from-slate-950 via-indigo-950 to-slate-950 flex items-center justify-center p-6"
            >
                <div className="max-w-md w-full text-center">
                    <motion.img
                        src={NANOUK.happy}
                        alt="Happy Nanouk"
                        className="w-32 h-32 mx-auto mb-6"
                        animate={{ y: [0, -10, 0] }}
                        transition={{ duration: 2, repeat: Infinity }}
                    />
                    <h1 className="text-3xl font-bold text-white mb-2">{t.completeTitle}</h1>
                    <p className="text-white/70 mb-6">{t.completeSubtitle}</p>
                    
                    {result && (
                        <div className="grid grid-cols-2 gap-4 mb-8">
                            <div className="bg-white/10 rounded-xl p-4">
                                <p className="text-3xl font-bold text-primary">{habits.days_to_fill}</p>
                                <p className="text-xs text-white/60">{t.daysGenerated}</p>
                            </div>
                            <div className="bg-white/10 rounded-xl p-4">
                                <p className="text-3xl font-bold text-violet-400">{result.total_entries}</p>
                                <p className="text-xs text-white/60">{t.entries}</p>
                            </div>
                        </div>
                    )}
                    
                    <Button
                        size="lg"
                        onClick={onComplete}
                        className="w-full bg-gradient-to-r from-cyan-500 to-violet-500"
                    >
                        {t.completeButton}
                    </Button>
                </div>
            </motion.div>
        );
    }

    if (loading) {
        return (
            <div className="min-h-screen bg-gradient-to-b from-slate-950 via-indigo-950 to-slate-950 flex items-center justify-center p-6">
                <div className="text-center">
                    <motion.img
                        src={NANOUK.curious}
                        alt="Nanouk"
                        className="w-32 h-32 mx-auto mb-6"
                        animate={{ rotate: [0, 5, -5, 0] }}
                        transition={{ duration: 1, repeat: Infinity }}
                    />
                    <Loader2 className="w-8 h-8 animate-spin text-primary mx-auto mb-4" />
                    <p className="text-white/70">{t.generating}</p>
                </div>
            </div>
        );
    }

    return (
        <div className="min-h-screen bg-gradient-to-b from-slate-950 via-indigo-950 to-slate-950 text-white flex items-center justify-center p-6">
            <div className="max-w-md w-full">
                {/* Header */}
                <div className="text-center mb-8">
                    <motion.img
                        src={NANOUK.curious}
                        alt="Nanouk"
                        className="w-24 h-24 mx-auto mb-4"
                        animate={{ y: [0, -5, 0] }}
                        transition={{ duration: 2, repeat: Infinity }}
                    />
                    <h1 className="text-2xl font-bold text-white mb-2">{t.title}</h1>
                    <p className="text-white/70 text-sm">{t.subtitle}</p>
                </div>

                {/* Progress */}
                <div className="flex gap-2 mb-8">
                    {steps.map((_, i) => (
                        <div
                            key={i}
                            className={`h-1 flex-1 rounded-full transition-all ${
                                i <= step ? 'bg-gradient-to-r from-cyan-500 to-violet-500' : 'bg-white/20'
                            }`}
                        />
                    ))}
                </div>

                {/* Step Content */}
                <AnimatePresence mode="wait">
                    <motion.div
                        key={step}
                        initial={{ opacity: 0, x: 20 }}
                        animate={{ opacity: 1, x: 0 }}
                        exit={{ opacity: 0, x: -20 }}
                        className="bg-white/5 backdrop-blur-sm rounded-2xl p-6 border border-white/10 text-white [&_label]:text-white/90 [&_.text-white/60]:text-white/60 [&_input]:text-white [&_input]:bg-white/10 [&_input]:border-white/20"
                    >
                        {/* Step Icon & Title */}
                        <div className="flex items-center gap-3 mb-6">
                            <div className={`w-10 h-10 rounded-xl bg-gradient-to-r ${currentStep.color} flex items-center justify-center`}>
                                <currentStep.icon className="w-5 h-5 text-white" />
                            </div>
                            <h2 className="text-lg font-semibold text-white">{currentStep.title}</h2>
                        </div>

                        {/* Content */}
                        <div className="text-white">
                            {currentStep.content}
                        </div>
                    </motion.div>
                </AnimatePresence>

                {/* Navigation */}
                <div className="flex gap-3 mt-6">
                    {step > 0 && (
                        <Button
                            variant="outline"
                            onClick={() => setStep(step - 1)}
                            className="flex-1"
                        >
                            <ChevronLeft className="w-4 h-4 mr-2" />
                            {t.back}
                        </Button>
                    )}
                    
                    {step < steps.length - 1 ? (
                        <Button
                            onClick={() => setStep(step + 1)}
                            className="flex-1 bg-gradient-to-r from-cyan-500 to-violet-500"
                        >
                            {t.next}
                            <ChevronRight className="w-4 h-4 ml-2" />
                        </Button>
                    ) : (
                        <Button
                            onClick={generateData}
                            className="flex-1 bg-gradient-to-r from-cyan-500 to-violet-500"
                        >
                            <Sparkles className="w-4 h-4 mr-2" />
                            {t.generate}
                        </Button>
                    )}
                </div>

                {/* Skip Option */}
                <Button
                    variant="ghost"
                    onClick={onComplete}
                    className="w-full mt-4 text-white/50 hover:text-white/70"
                >
                    {t.skip}
                </Button>
            </div>
        </div>
    );
}
